/**
 * Add or edit one shared ServiceNow table feed for a Workspace Vault.
 *
 * The table becomes immutable once connected because its sys_id checkpoints
 * own imported document identity. The dialog presents connection, bounded
 * scope, and cadence without changing the provider's durable feed contract.
 */
import { useEffect, useState } from 'react'
import { Database, Loader2, Trash2 } from 'lucide-react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui'
import { connectorScheduleLabels } from '../../components/ConnectorScheduleSelect'
import { ConnectorDialogSection } from '../../components/ConnectorDialogSection'
import { ConnectorQueryLockNotice } from '../../components/ConnectorQueryLockNotice'
import {
  ConnectorSourceDisplayFieldSelection,
  orderedSourceDisplayFields,
} from '../../components/ConnectorSourceDisplayFieldSelection'
import type { ConnectorFeedFor } from '../../types'
import { useServiceNowFeedForm } from '../hooks/useServiceNowFeedForm'
import { serviceNowTableLabels } from '../types'
import { ServiceNowCredentials } from './ServiceNowCredentials'
import { ServiceNowSyncOptions } from './ServiceNowSyncOptions'
import { ServiceNowTableScope } from './ServiceNowTableScope'

function summarizeApiUrl(value: string): string {
  try {
    return new URL(value).host
  } catch {
    return value || 'Instance URL required'
  }
}

export function ServiceNowFeedDialog({
  api,
  orgId,
  vaultId,
  feed,
  canEditQuery,
  canManageCredentials,
  open,
  onOpenChange,
  onConfigured,
  onSyncStarted,
}: {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  vaultId: string
  feed?: ConnectorFeedFor<'servicenow'> | null
  // Server-derived ServiceNow query capability. Table, filter, and preview
  // edits require a connector administrator grant; without it the dialog is
  // read-only so the save cannot reach a guaranteed 403.
  canEditQuery: boolean
  canManageCredentials: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfigured: () => Promise<void>
  onSyncStarted: () => void
}) {
  const form = useServiceNowFeedForm({
    api,
    orgId,
    vaultId,
    feed,
    open,
    canEditQuery,
    canAdministerWorkspace: canManageCredentials,
    onConfigured,
    onSyncStarted,
  })
  const [sections, setSections] = useState({
    connection: false,
    scope: true,
    display: false,
    sync: false,
  })
  const [reviewed, setReviewed] = useState(false)

  useEffect(() => {
    if (!open) return
    setSections({ connection: false, scope: true, display: false, sync: false })
  }, [feed, open])

  useEffect(() => {
    setReviewed(false)
  }, [form.apiUrl, form.filters, form.initialSync, form.recordLimit, form.table, open])

  const setSection = (section: keyof typeof sections, sectionOpen: boolean) => {
    setSections(current => ({ ...current, [section]: sectionOpen }))
  }
  const credentialLabel = form.credentialSource === 'workspace'
    ? 'Workspace credential'
    : 'UMD Credential Store'
  const credentialStored = form.credentialSource === 'workspace'
    ? form.connection.workspaceConfigured
    : form.connection.umdConfigured
  const tableLabel = serviceNowTableLabels[form.table]
  const scheduleLabel = connectorScheduleLabels[form.schedule]
  const matchingRecords = form.selectionPreview?.matchCount
  const filterSummary = `${form.filters.length} filter${form.filters.length === 1 ? '' : 's'}`
  const displaySummary = form.sourceDisplayFieldKeys.length
    ? orderedSourceDisplayFields(
        form.sourceDisplayFields,
        form.sourceDisplayFieldKeys,
      )
        .map(field => field.label)
        .join(', ')
    : 'No dashboard fields'
  return (
    <>
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!form.saving) onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-[14px] p-0 shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:max-w-[1120px]">
        <DialogHeader className="shrink-0 border-b border-slate-100 px-6 py-4 pr-12 text-left">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] bg-slate-50 text-slate-700">
              <Database className="h-[18px] w-[18px]" />
            </span>
            <div>
              <DialogTitle className="text-[16.5px] font-semibold leading-5 text-slate-950">
                ServiceNow table source
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[13px] leading-4 text-slate-500">
                Imports a bounded window of records from one approved table.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <ConnectorDialogSection
            title="Connection"
            summary={`${summarizeApiUrl(form.apiUrl)} · ${credentialLabel}${credentialStored ? ' · ✓ credentials stored' : ' · not configured'}`}
            open={sections.connection}
            onOpenChange={sectionOpen => setSection('connection', sectionOpen)}
          >
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="servicenow-api-url" className="text-xs font-medium text-slate-700">
                  Instance URL
                </Label>
                <Input
                  id="servicenow-api-url"
                  type="url"
                  autoComplete="url"
                  value={form.apiUrl}
                  disabled={!canEditQuery}
                  onChange={event => form.setApiUrl(event.target.value)}
                  placeholder="https://instance.service-now.com"
                  className="h-9 font-mono text-[13px]"
                />
                <p className="text-xs text-slate-400">
                  ServiceNow instance root with no path, query, or fragment.
                </p>
              </div>
              <ServiceNowCredentials
                source={form.credentialSource}
                onSourceChange={form.setCredentialSource}
                connection={form.connection}
                credentials={form.credentials}
                onCredentialsChange={form.setCredentials}
                canSelectSource={canEditQuery}
                canManageCredential={canManageCredentials}
              />
            </div>
          </ConnectorDialogSection>

          <ConnectorDialogSection
            title="Dashboard fields"
            summary={displaySummary}
            open={sections.display}
            onOpenChange={sectionOpen => setSection('display', sectionOpen)}
          >
            <ConnectorSourceDisplayFieldSelection
              fields={form.sourceDisplayFields}
              selectedKeys={form.sourceDisplayFieldKeys}
              onSelectedKeysChange={form.setSourceDisplayFieldKeys}
              disabled={!canManageCredentials}
            />
          </ConnectorDialogSection>

          <ConnectorDialogSection
            title="Table & scope"
            summary={`${tableLabel} · ${filterSummary}${matchingRecords === undefined ? '' : ` · ${matchingRecords} matching records`}`}
            open={sections.scope}
            onOpenChange={sectionOpen => setSection('scope', sectionOpen)}
          >
            <ServiceNowTableScope
              table={form.table}
              onTableChange={form.setTable}
              recordLimit={form.recordLimit}
              onRecordLimitChange={form.setRecordLimit}
              filters={form.filters}
              onFiltersChange={form.setFilters}
              definitions={form.tableDefinitions}
              accessCatalog={form.tableAccess}
              loadingTables={form.loadingTables}
              loadingTableAccess={form.loadingTableAccess}
              onLoadTableAccess={form.loadTableAccess}
              selectionPreview={form.selectionPreview}
              loadingSelectionPreview={form.loadingSelectionPreview}
              selectionPreviewError={form.selectionPreviewError}
              initialSync={form.initialSync}
              onInitialSyncChange={form.setInitialSync}
              editing={form.editing}
              canEditQuery={canEditQuery}
            />
          </ConnectorDialogSection>

          <ConnectorDialogSection
            title="Sync schedule"
            summary={`${scheduleLabel} · new or changed sys_id revisions only`}
            open={sections.sync}
            onOpenChange={sectionOpen => setSection('sync', sectionOpen)}
          >
            <ServiceNowSyncOptions
              schedule={form.schedule}
              onScheduleChange={form.setSchedule}
              disabled={!canManageCredentials}
            />
          </ConnectorDialogSection>
        </div>

        <div className="shrink-0 border-t border-slate-100 bg-slate-50/80">
          {canEditQuery && <label className="flex cursor-pointer items-start gap-2.5 border-b border-slate-100 bg-white px-6 py-3 text-xs leading-5 text-slate-600">
            <Checkbox
              aria-label="I reviewed the ServiceNow selection"
              className="mt-0.5"
              checked={reviewed}
              disabled={!form.selectionPreview || form.loadingSelectionPreview}
              onCheckedChange={checked => setReviewed(checked === true)}
            />
            <span>
              I reviewed the selection — connect{' '}
              <strong className="font-semibold text-slate-800">
                {tableLabel} · {matchingRecords ?? '—'} matching record{matchingRecords === 1 ? '' : 's'}
              </strong>
              . Sync transfers the records and starts their configured AI analysis.
            </span>
          </label>}

          {form.preview && (
            <div className="border-b border-slate-100 px-6 py-3 text-xs leading-5 text-slate-600">
              This selection contains{' '}
              <strong className="font-semibold text-slate-800">
                {form.preview.itemCount.toLocaleString()} record{form.preview.itemCount === 1 ? '' : 's'} from {tableLabel}
              </strong>
              {form.preview.runMode === 'baseline'
                ? '. DocReview will record the current state and sync future changes automatically.'
                : '. DocReview will synchronize this history and run configured analysis.'}
            </div>
          )}

          {!canEditQuery && (
            <div className="border-b border-slate-100 px-6 py-3">
              <ConnectorQueryLockNotice providerLabel="ServiceNow" />
            </div>
          )}

          <DialogFooter className="px-6 py-4 sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="justify-start px-0 text-xs text-destructive hover:bg-transparent hover:text-destructive"
              disabled={
                !canManageCredentials
                || form.credentialSource !== 'workspace'
                || !form.connection.workspaceConfigured
                || form.saving
              }
              onClick={() => void form.deleteCredential()}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete stored credentials
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={form.saving}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={
                  form.saving
                  || form.loadingConnection
                  || form.loadingTables
                  || (canEditQuery && (
                    form.loadingSelectionPreview
                    || !form.apiUrl.trim()
                    || !form.selectionPreview
                    || !reviewed
                    || !Number.isInteger(form.recordLimit)
                    || form.recordLimit < 1
                    || form.recordLimit > 500
                  ))
                }
                onClick={() => void form.submit()}
              >
                {form.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {!canEditQuery
                  ? 'Save changes'
                  : !form.preview
                    ? 'Check selection'
                    : form.editing ? 'Save changes' : 'Connect source'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
